import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  Button,
  Checkbox, IconButton, InputAdornment, LinearProgress, ListItem, ListSubheader, MenuList, Popover, TextField,
} from '@mui/material';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import OptionsIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { IAppState } from '../../../state/reducers';
import {
  IDispatchProps, IProps, IStateProps,
} from './interfaces';
import {
  addTag, loadOrgTags, selectOrgTagsLoaded, selectIsAdminTagsLoaded, selectTagsByOrgId, selectAllTags, tagAsset, untagAsset, setCurrentAssetId, loadAssetTags, selectCurrentAssetTags, updateAssetTags, removeTag, editTag, selectCurrentAssetTagsLoaded, loadIndeterminateTags, selectCurrentIndeterminateTags,
} from '../../../state/tags';
import { ITag, ITagAsset, ITagCreate } from '../../../api/vault/tags';
import {
  CreateTagButton, EditTagContent, EditText, ListItemIconTest, RenameContainer, TagMenu,
} from './styles';
import { selectUserOrgIds } from '../../../state/user/userOrgRole';
import toast from '../../../utils/toast';

const confirmDeleteColor = 'rgb(218,23,23)';
const confirmDeleteColorHover = 'rgba(218,23,23, 0.8)';
const confirmDeleteTextColor = 'white';
const reanmeDeleteTextColor = 'black';

const AddTagsMenu: FC<IProps> = (props: IProps) => {
  const {
    orgTags,
    orgTagsLoaded,
    adminTags,
    isAdminTagsLoaded,
    userOrgs,
    currentAssetTags,
    currentIndeterminateTags,
    currentAssetTagsLoaded,
    getOrgTags,
    addTag,
    removeTag,
    editTag,
    addTagToAsset,
    removeTagFromAsset,
    setCurrentAssetId,
    loadCurrentAssetTags,
    loadCurrentIndeterminateTags,
    addTagButton: AddTagButton,
    assetTags,
    indeterminateTags,
    assetId,
    addTagsAnchorEl,
    buttonText,
    buttonIcon,
    buttonDisabled,
    bulkTags,
    onBulkTagCallback,
  } = props;

  const [renameText, setRenameText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openRename, setOpenRename] = useState(false);
  const [currentEditTag, setEditTag] = useState<null | ITag>(null);
  const [editAnchorEl, setEditAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(addTagsAnchorEl);
  const [filterText, setFilterText] = useState('');
  const isOpen = Boolean(anchorEl);
  const isEditOpen = Boolean(editAnchorEl);
  const editId = isEditOpen ? 'simple-popover' : undefined;

  const closeEditTag = () => {
    setEditAnchorEl(null);
    setEditTag(null);
    setRenameText('');
    setConfirmDelete(false);
    setOpenRename(false);
  };

  const handleClose = () => {
    setAnchorEl(
      null,
    );
    if (typeof onBulkTagCallback === 'function') {
      onBulkTagCallback();
    }
  };

  const handleOpenEditTags = (e: any, tag: ITag) => {
    setEditAnchorEl(e.currentTarget);
    setEditTag(tag);
    setRenameText(tag.name);
  };

  const handleClickOffDelete = () => {
    if (confirmDelete) {
      setConfirmDelete(false);
    }
  };

  const renderPopover = () => {
    const handleClickDeleteTag = () => {
      if (confirmDelete) {
        // Delete
        closeEditTag();
        removeTag(currentEditTag._id);
      } else {
        setConfirmDelete(true);
      }
    };

    const handleClickRenameTag = () => {
      setOpenRename(!openRename);
    };

    const onChangeRenameText = (e: any) => {
      if (e.target.value !== renameText) {
        setRenameText(
          e.target.value,
        );
      }
    };

    const onPressEnter = (e: any) => {
      e.stopPropagation();
      if (e.key === 'Enter' && renameText.length > 0) {
        // Check if already renamed
        const foundTag = [...orgTags].find((value) => value.name === renameText);
        if (foundTag && foundTag._id !== currentEditTag._id) {
          toast('Tag name is already in use', {
            type: toast.TYPE.ERROR,
          });
        } else {
          // Save and close
          closeEditTag();
          editTag(currentEditTag._id, { name: renameText });
        }
      }
    };

    const onRenameClick = () => {
      if (renameText.length > 0) {
        // Check if already renamed
        const foundTag = [...orgTags].find((value) => value.name === renameText);
        if (foundTag && foundTag._id !== currentEditTag._id) {
          toast('Tag name is already in use', {
            type: toast.TYPE.ERROR,
          });
        } else {
          // Save and close
          closeEditTag();
          editTag(currentEditTag._id, { name: renameText });
        }
      }
    };

    return (
      <Popover
        id={editId}
        open={isEditOpen}
        anchorEl={editAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={closeEditTag}
      >
        <EditTagContent onClick={handleClickOffDelete}>
          <Button
            startIcon={<EditIcon />}
            onClick={handleClickRenameTag}
            sx={{
              justifyContent: 'flex-start',
              color: reanmeDeleteTextColor,
            }}
          >
            <EditText>Rename</EditText>
          </Button>
          {openRename && (
            <RenameContainer>
              <TextField
                autoFocus
                onChange={onChangeRenameText}
                onKeyDown={onPressEnter}
                value={renameText}
                variant="outlined"
                InputProps={{
                  inputProps: {
                    style:
                    {
                      width: '100%', height: 0.2, fontSize: '14px', padding: '20px',
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        style={{ padding: '0px' }}
                        size="small"
                        onClick={onRenameClick}
                      >
                        <CheckIcon />
                      </IconButton>
                    </InputAdornment>),
                }}
              />
            </RenameContainer>
          )}
          <Button
            startIcon={<DeleteIcon sx={{ color: confirmDelete ? 'white' : '' }} />}
            onClick={handleClickDeleteTag}
            sx={{
              background: confirmDelete ? confirmDeleteColor : '',
              color: confirmDelete ? confirmDeleteTextColor : reanmeDeleteTextColor,
              justifyContent: 'flex-start',
              ':hover': {
                ...(confirmDelete && { bgcolor: confirmDeleteColorHover }),
              },
            }}
          >
            <EditText>{confirmDelete ? 'Confirm' : 'Delete'}</EditText>
          </Button>
        </EditTagContent>
      </Popover>
    );
  };

  const handleCreateNewTag = async () => {
    if (userOrgs.length > 1) {
      console.warn(`[QA] User belongs to multiple orgs -> targeting "${userOrgs[0]}"`);
    }
    const params: ITagCreate = {
      name: filterText,
      orgId: userOrgs[0],
    };
    addTag(params);
  };

  const handleCheckBoxClick = async (tag: ITag) => {
    let updatedTags = [...currentAssetTags];
    let updatedIndTags = [...currentIndeterminateTags];
    if (isTagSelected(tag)) {
      updatedTags = updatedTags.filter((oldTag) => oldTag._id !== tag._id);
      const params: ITagAsset = {
        assetIds: assetId,
        tagIds: [tag._id],
      };
      removeTagFromAsset(assetId, tag, updatedTags);
    } else if (isTagIndeterminate(tag)) {
      updatedIndTags = updatedIndTags.filter((oldTag) => oldTag._id !== tag._id);
      updatedTags.push(tag);
      addTagToAsset(assetId, tag, updatedTags, updatedIndTags);
    } else {
      updatedTags.push(tag);
      addTagToAsset(assetId, tag, updatedTags);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!orgTagsLoaded) {
      getOrgTags();
    }
    if (assetId.length === 1) {
      setCurrentAssetId(assetId[0]);
    }
    loadCurrentAssetTags(assetTags);
    if (indeterminateTags != null && indeterminateTags.length > 0) {
      loadCurrentIndeterminateTags(indeterminateTags);
    }
    setAnchorEl(
      e.currentTarget,
    );
  };

  const isTagSelected = (tag: ITag) => (!!currentAssetTags.find((checkedTag) => checkedTag._id === tag._id));

  const isTagIndeterminate = (tag: ITag) => (!!currentIndeterminateTags.find((checkedTag) => checkedTag._id === tag._id));

  const renderTextFilter = () => {
    const handleTextFilter = (e: { target: { value: React.SetStateAction<string>; }; }) => {
      if (e.target.value !== filterText) {
        setFilterText(
          e.target.value,
        );
      }
    };

    return (
      <TextField
        autoFocus
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="Search Tags"
        variant="outlined"
        fullWidth
        onChange={handleTextFilter}
        value={filterText}
        InputProps={{
          'aria-label': 'bare',
          style: {
            width: '100%', height: 0.2, fontSize: '14px', padding: '20px', position: 'sticky',
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
          inputProps: {
            style: { padding: '0px' },
          },
        }}
      />
    );
  };

  const renderCreateNewTag = () => {
    const tagMatches = [...orgTags].filter((tag) => filterText === '' || tag.name === (filterText));
    if (filterText !== '' && tagMatches.length === 0) {
      return (
        <ListItem key={filterText} dense>
          <ListItemText id={`new-tag-${filterText}`} primary={filterText} />
          <ListItemSecondaryAction>
            <CreateTagButton
              onClick={handleCreateNewTag}
            >
              CREATE TAG
            </CreateTagButton>
          </ListItemSecondaryAction>
        </ListItem>
      );
    }
    return null;
  };

  const renderFilteredTags = () => {
    const filteredTags = [...orgTags].filter((tag) => filterText === '' || tag.name.includes(filterText));
    const tagList = filteredTags.map((tag: ITag, index: number) => {
      const labelId = `checkbox-list-tag-${tag.name}`;
      const checkBoxId: string = `${tag._id}_checkbox`;
      return (
        <ListItem key={index} dense>
          <ListItemIconTest>
            <Checkbox
              checked={isTagSelected(tag)}
              onChange={(e) => handleCheckBoxClick(tag)}
              color="secondary"
              id={checkBoxId}
              edge="start"
              disableRipple
              inputProps={{ 'aria-labelledby': labelId, style: { minWidth: 'auto' } }}
            />
          </ListItemIconTest>
          <ListItemText id={labelId} primary={(tag.name)} />
          <p>{tag.totalAssets ? tag.totalAssets : 0} items</p>
          <ListItemSecondaryAction>
            <IconButton edge="end" aria-label="comments" onClick={(e) => handleOpenEditTags(e, tag)} disabled={isEditOpen}>
              <OptionsIcon color="secondary" />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      );
    });
    return tagList;
  };

  const renderFilteredBulkTags = () => {
    const filteredTags = [...orgTags].filter((tag) => filterText === '' || tag.name.includes(filterText));
    const tagList = filteredTags.map((tag: ITag, index: number) => {
      const labelId = `checkbox-list-tag-${tag.name}`;
      const checkBoxId: string = `${tag._id}_checkbox`;
      return (
        <ListItem key={index} dense>
          <ListItemIconTest>
            <Checkbox
              checked={isTagSelected(tag)}
              indeterminate={(isTagIndeterminate(tag))}
              color="secondary"
              onChange={(e) => handleCheckBoxClick(tag)}
              id={checkBoxId}
              edge="start"
              tabIndex={-1}
              disableRipple
              inputProps={{ 'aria-labelledby': labelId, style: { minWidth: 'auto' } }}
            />
          </ListItemIconTest>
          <ListItemText id={labelId} primary={(tag.name)} />
          <p>{tag.totalAssets ? tag.totalAssets : 0} items</p>
          <ListItemSecondaryAction>
            <IconButton edge="end" aria-label="comments" onClick={(e) => handleOpenEditTags(e, tag)} disabled={isEditOpen}>
              <OptionsIcon color="secondary" />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      );
    });
    return tagList;
  };

  return (
    <div>
      <AddTagButton
        onClick={handleClick}
        id="addTags"
        disabled={buttonDisabled}
      >
        {buttonText}
        {buttonIcon}
      </AddTagButton>
      {isOpen && (
        <TagMenu
          MenuListProps={{ autoFocus: false, disablePadding: true, style: { maxHeight: '270px', overflow: 'auto' } }}
          id="simple-menu"
          data-auto="TagMenuList"
          anchorEl={anchorEl}
          // getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          open={isOpen}
          onClose={handleClose}
        >
          <ListSubheader disableGutters>
            {/* {(!orgTagsLoaded || !currentAssetTagsLoaded) && <LinearProgress />} */}
            {(!orgTagsLoaded) && <LinearProgress />}
            {renderTextFilter()}
          </ListSubheader>
          <MenuList disablePadding>
            {(bulkTags != null && bulkTags.length > 0) ? renderFilteredBulkTags() : renderFilteredTags()}
            {renderCreateNewTag()}
          </MenuList>
          {isEditOpen && renderPopover()}
        </TagMenu>
      )}
    </div>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  orgTags: selectTagsByOrgId(state),
  orgTagsLoaded: selectOrgTagsLoaded(state),
  adminTags: selectAllTags(state),
  isAdminTagsLoaded: selectIsAdminTagsLoaded(state),
  userOrgs: selectUserOrgIds(state),
  currentAssetTags: selectCurrentAssetTags(state),
  currentIndeterminateTags: selectCurrentIndeterminateTags(state),
  currentAssetTagsLoaded: selectCurrentAssetTagsLoaded(state),
});

const mapDispatchToProps: IDispatchProps = {
  addTag,
  getOrgTags: loadOrgTags,
  addTagToAsset: tagAsset,
  removeTagFromAsset: untagAsset,
  setCurrentAssetId,
  loadCurrentAssetTags: loadAssetTags,
  loadCurrentIndeterminateTags: loadIndeterminateTags,
  removeTag,
  editTag,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(AddTagsMenu));
